import { MinimalErrorLayout } from "@/components/MinimalErrorLayout";
import { SALES_CONTACT_EMAIL_FALLBACK } from "@/lib/salesContact";

// TunableSetting keys read by ProfileProvider. Kept here next to the screen they drive.
export const OFFBOARDED_SETTING_KEY = "tenant.offboarded";
export const OFFBOARDED_MESSAGE_SETTING_KEY = "tenant.offboarded-message";
// Why the tenant was offboarded. "contract" (default, unset/anything else) is a
// churned tenant and loses the whole app. "testphase" is a trial that ran out: that is
// a pause, not a goodbye, so the tenant keeps every page that reads data it already
// owns and loses only the AI surfaces (the /agent/* routes — in Neptune the whole
// panel, which is the service assistant). Jupiter's TenantOffboardingGate draws the same
// line server-side, see jupiter/gql/extensions.py; keep the two in step.
export const OFFBOARDED_REASON_SETTING_KEY = "tenant.offboarded-reason";
export const OFFBOARDED_REASON_TESTPHASE = "testphase";

const COPY = {
  contract: {
    title: "Schade, dass Sie gehen",
    defaultMessage:
      "Ihr lytra-Zugang wurde deaktiviert, da der Vertrag Ihres Unternehmens mit uns beendet wurde.",
    contactPrompt: "Sie möchten lytra weiter nutzen oder Ihre Daten exportieren? Schreiben Sie uns an",
  },
  testphase: {
    title: "Testphase beendet",
    defaultMessage:
      "Ihr lytra-Zugang wurde vorrübergehend deaktiviert, da die Testphase ausgelaufen ist.",
    contactPrompt: "Sie möchten lytra weiter nutzen? Schreiben Sie uns an",
  },
} as const;

interface TenantOffboardedScreenProps {
  tenantName?: string | null;
  message?: string;
  contactEmail?: string;
  reason?: string;
}

/**
 * Full-screen notice shown instead of the app when a tenant has been offboarded.
 *
 * Rendered by ProfileProvider before any route, so no page of the app is reachable.
 * MinimalErrorLayout keeps the logo and the logout menu available, so a user with
 * access to several tenants can still switch accounts.
 */
export function TenantOffboardedScreen({
  tenantName,
  message,
  contactEmail = SALES_CONTACT_EMAIL_FALLBACK,
  reason,
}: TenantOffboardedScreenProps) {
  const copy =
    reason === OFFBOARDED_REASON_TESTPHASE ? COPY.testphase : COPY.contract;
  return (
    <MinimalErrorLayout>
      <div className="max-w-xl mx-auto p-10 bg-white dark:bg-gray-800 rounded-lg shadow-lg text-center">
        {/* Gradient rule in the brand sequence: purple -> cyan -> yellow. */}
        <div
          className="h-1.5 w-24 mx-auto mb-8 rounded-full"
          style={{
            background:
              "linear-gradient(90deg, #D571E5 0%, #80E4FB 55%, #F5F886 100%)",
          }}
        />
        <h1
          className="text-2xl font-semibold mb-4 text-[#07195F] dark:text-gray-100"
          style={{ fontFamily: "Sora, system-ui, sans-serif" }}
        >
          {copy.title}
        </h1>
        <p className="text-gray-600 dark:text-gray-300 mb-6 leading-relaxed">
          {/* A whitespace-only message trims to "" and must fall through too. */}
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {message?.trim() || copy.defaultMessage}
        </p>
        {tenantName ? (
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            Betroffenes Konto: <span className="font-medium">{tenantName}</span>
          </p>
        ) : null}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {copy.contactPrompt}{" "}
          <a
            href={`mailto:${contactEmail}`}
            className="font-medium text-[#345AEF] hover:underline"
          >
            {contactEmail}
          </a>
          .
        </p>
      </div>
    </MinimalErrorLayout>
  );
}

/**
 * In-page version of the notice above, for a *paused* tenant (reason "testphase").
 *
 * A pause only withholds the AI surfaces, so the app shell, sidebar and every other
 * page stay reachable — this renders inside the layout, where the service assistant or
 * the chat would have been, rather than replacing the whole screen.
 */
export function TenantPausedNotice({
  contactEmail = SALES_CONTACT_EMAIL_FALLBACK,
  message,
}: {
  contactEmail?: string;
  message?: string;
}) {
  return (
    <div className="flex-1 flex items-center justify-center p-8">
      <div className="max-w-lg w-full p-8 bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 text-center">
        {/* Gradient rule in the brand sequence: purple -> cyan -> yellow. */}
        <div
          className="h-1.5 w-20 mx-auto mb-6 rounded-full"
          style={{
            background:
              "linear-gradient(90deg, #D571E5 0%, #80E4FB 55%, #F5F886 100%)",
          }}
        />
        <h2
          className="text-xl font-semibold mb-3 text-[#07195F] dark:text-gray-100"
          style={{ fontFamily: "Sora, system-ui, sans-serif" }}
        >
          {COPY.testphase.title}
        </h2>
        <p className="text-gray-600 dark:text-gray-300 mb-4 leading-relaxed">
          {/* A whitespace-only message trims to "" and must fall through too. */}
          {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
          {message?.trim() ||
            "Ihre Testphase ist ausgelaufen. Der KI-Assistent und der Chat sind vorübergehend deaktiviert — Ihre übrigen Seiten und Daten bleiben wie gewohnt verfügbar."}
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {COPY.testphase.contactPrompt}{" "}
          <a
            href={`mailto:${contactEmail}`}
            className="font-medium text-[#345AEF] hover:underline"
          >
            {contactEmail}
          </a>
          .
        </p>
      </div>
    </div>
  );
}
